import { useEffect, useState, useCallback } from 'react';
import { useWebSockets } from './useWebSockets';
import { TagResponse } from 'src/features/alarm-rules/types';
import { convertToNumber, getIndexOfLastElement } from '../utils';
import { subMinutes, addMinutes } from 'date-fns';
import { abs } from 'stylis';
import { GetAllReadingItem } from 'src/services/readings/types';

type useTrendWidgetDataArgumentsTypes = {
  gatewayInput: TagResponse;
  customerId: string | null;
  readings?: GetAllReadingItem[];
};

export const useTrendWidgetData = ({
  gatewayInput,
  customerId,
  readings,
}: useTrendWidgetDataArgumentsTypes) => {
  const [inputData, setInputData] = useState<any[] | null>(null);
  const gatewayId = gatewayInput?.gatewayId;
  const { messages } = useWebSockets(gatewayId!);
  const indexOfLastElement = getIndexOfLastElement(messages);
  const dataSocket = messages[indexOfLastElement];
  const timestamp = Number(dataSocket?.payload?.data.onCreatedReading?.timestamp);
  const gatewayInputs = dataSocket?.payload.data.onCreatedReading.gateway_inputs;
  const findInput = gatewayInputs?.find(
    (input: { id: string | undefined }) => input.id === gatewayInput?.id
  );

  const toLocalISOString = (dateString: string) => {
    const offset = new Date().getTimezoneOffset();
    return offset < 0
      ? addMinutes(new Date(dateString.replace('Z', '')), abs(offset)).toString()
      : subMinutes(new Date(dateString.replace('Z', '')), offset).toString();
  };

  useEffect(() => {
    if (findInput && readings) {
      setInputData((prevState: any[] | null) => {
        if (prevState === null) {
          return [
            {
              inputName: findInput.displayName,
              rawUnit: findInput.rawUnit,
              timestamp: new Date(timestamp * 1000).toString(),
              input: convertToNumber(findInput.value),
            },
          ];
        }
        return [
          {
            inputName: findInput.displayName,
            rawUnit: findInput.rawUnit,
            timestamp: new Date(timestamp * 1000).toString(),
            input: convertToNumber(findInput.value),
          },
          ...prevState,
        ];
      });
    }
  }, [findInput, timestamp, readings]);

  const getWidgetData = useCallback(async () => {
    if (!readings) {
      setInputData(null);
      return;
    }
    if (readings.length === 0) {
      setInputData([]);
      return;
    }
    const mappedReadings: any[] | null = [];
    let lastValidInputName = '';
    let lastValidRawUnit = '';
    let lastValidInput: number | undefined = undefined;
    readings
      .filter((x) => x.gatewayId === gatewayId)
      .forEach((item) => {
        const input = item.gatewayInputs.find((input) => input.id === gatewayInput?.id);
        if (input) {
          lastValidInput = input.value;
          lastValidInputName = input.displayName;
          lastValidRawUnit = input.rawUnit ?? '';
          mappedReadings.push({
            timestamp: toLocalISOString(new Date(item.timestamp * 1000).toISOString()),
            inputName: input?.displayName,
            input: input?.value,
            rawUnit: input?.rawUnit,
          });
        } else {
          if (lastValidInput) {
            mappedReadings.push({
              timestamp: toLocalISOString(new Date(item.timestamp * 1000).toISOString()),
              inputName: lastValidInputName,
              input: lastValidInput,
              rawUnit: lastValidRawUnit,
            });
          }
        }
      });
    setInputData(mappedReadings);
  }, [gatewayInput, readings, gatewayId]);

  useEffect(() => {
    if (customerId && gatewayId && inputData === null) {
      getWidgetData();
    }
  }, [customerId, gatewayId, inputData, getWidgetData]);

  return { inputData };
};
